import {
  QueryGenqlSelection,
  Query,
  FieldsSelection,
} from '@rematter/frontend-lib-api-client';
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import camelCase from 'lodash/camelCase';

import { useApi } from '~/shared/hooks';

export type QueryHookResult<T> = T extends (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...args: any
) => UseQueryResult<infer U, infer _E>
  ? U
  : never;

export type QueryHookOptions<TData, TResult> = Omit<
  UseQueryOptions<TData, Error, TResult>,
  'queryKey' | 'queryFn'
> & {
  alias?: string;
};

export function createQueryHook<
  TParams,
  TSelection extends QueryGenqlSelection,
  TData = FieldsSelection<Query, TSelection>,
  TResult = TData,
>(
  key: `${string}.${string}`, // 'transportation.trucks'
  selectionFn: (params: TParams) => TSelection,
  options?: Omit<QueryHookOptions<TData, TResult>, 'alias'>,
) {
  const queryHook = function useGenqlQuery(
    params?: TParams & {
      queryOptions?: Omit<
        UseQueryOptions<TData, Error, TResult>,
        'queryKey' | 'queryFn'
      >;
    },
  ): UseQueryResult<TResult, Error> {
    const { client } = useApi();

    const queryKey = [key, params];
    const queryOptions = params?.queryOptions;

    return useQuery<TData, Error, TResult>({
      queryKey,
      queryFn: async () => {
        try {
          const selection = selectionFn(params ?? ({} as TParams));
          const result = await client(`query.${key}`).query({
            ...selection,
            __name: camelCase(key),
          });
          return result as TData;
        } catch (error) {
          throw error instanceof Error ? error : new Error(String(error));
        }
      },
      ...options,
      ...queryOptions,
    });
  };

  queryHook.__queryKey = key;

  return queryHook;
}
